import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { post, postApi, putApi, urlConfig } from "../../api/getUser";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { useHistory } from "react-router";
import {
  AddFormButton,
  FilterButton,
} from "../../shared/components/TableElements";
import {
  colorBlue,
  colorGray,
  sidenavColor,
  sidenavLinkHoverColor,
  colorYellowNew,
} from "../../utils/palette";
import { left, right, marginRight, textAlign } from "../../utils/directions";
import SampleTable from "./SampleTable";
import useLazyWebSocket from "../../shared/custom-hooks/useLazyWebsocket";
import TableModal from "./TableModal";
import useAutosizeTextArea from "../../shared/custom-hooks/useAutosizeTextArea";
import SaveReportModal from "./SaveReportModal";
import ContactSupportModal from "./ContactSupportModal";
import { auth } from "../../api/firebase";
import axios from "axios";
import { HintText, LinkSpan } from "../../shared/components/form/FormElements";
import { useAuth } from "../../contexts/AuthContext";
import { getListingDisplayDate } from "../../shared/helpers";
import ExtraCreditsModal from "./ExtraCreditsModal";
import TopUpCreditsModal from "../Billing/ExistingPlan/components/TopUpCreditsModal";
import InfoPopup from "../../shared/components/InfoPopup";

const CreateReport = ({
  activeKey,
  editReport,
  clearEditReport,
  fetchReports,
  setActiveKey,
  messages,
  setMessages,
  userInput,
  setUserInput,
  query,
  conversationId,
  setConversationId,
  tableData,
  setTableData,
  hasMore,
  setHasMore,
  showChat,
  setShowChat,
}) => {
  const { t } = useTranslation("common");
  const history = useHistory();

  const theme = useTheme();
  const { orgId, userId } = useSelector((state) => state.user);
  const { tag } = useAuth();

  //const [messages, setMessages] = useState([]);
  //const [userInput, setUserInput] = useState("");
  //const [conversationId, setConversationId] = useState("");
  //const [query, setQuery] = useState("");
  //const [tableData, setTableData] = useState([]);
  //const [chatStarted, setChatStarted] = useState(false);
  //If tableData has > 1000 rows
  //const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(false);
  const [viewAllLoading, setViewAllLoading] = useState(false);

  const [showTableModal, setShowTableModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showExtraCreditsModal, setShowExtraCreditsModal] = useState(false);
  const [showSubscribeForCreditsModal, setShowSubscribeForCreditsModal] =
    useState(false);
  const [showSaveReport, setShowSaveReport] = useState(false);
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [supportSaveBtnLoading, setSupportSaveBtnLoading] = useState(false);
  //const [creditsSaveBtnLoading, setCreditsSaveBtnLoading] = useState(false);

  const [queryCredits, setQueryCredits] = useState(0);
  const [creditsAddMsg, setCreditsAddMsg] = useState("");
  const [isAutoTopupInProgress, setIsAutoTopupInProgress] = useState(false);

  const textAreaRef = useRef(null);
  useAutosizeTextArea(textAreaRef.current, userInput);
  const bottomRef = useRef(null);

  const [loadingPhase, setLoadingPhase] = useState(t("report_loading_msg_1"));
  const loadingSteps = [t("report_loading_msg_2"), t("report_loading_msg_3")];
  const pendingMessageRef = useRef(null);
  const isIntentionalClosure = useRef(false);

  const examples = [
    t("reporting_ex_1"),
    t("reporting_ex_2"),
    t("reporting_ex_3"),
    t("reporting_ex_4"),
  ];

  const webSocketApi =
    process.env.REACT_APP_ENV === "test"
      ? "wss://z6mj7lf198.execute-api.ap-south-1.amazonaws.com/test/"
      : "wss://z0wnfiykl6.execute-api.us-east-1.amazonaws.com/prod/";

  const {
    connect,
    disconnect,
    sendMessage,
    lastMessage,
    isConnected,
    connectionError,
  } = useLazyWebSocket(webSocketApi, {
    inactivityTimeout: 70000,
    onOpen: () => {
      console.log("🔌 WebSocket opened, ready to send messages");
      // Send any pending message
      if (pendingMessageRef.current) {
        console.log("📤 Sending pending message");
        sendMessage(pendingMessageRef.current);
        pendingMessageRef.current = null;
      }
    },
    onClose: () => {
      console.log("❌ WebSocket closed");
      if (loading && !isIntentionalClosure.current) {
        setLoading(false);
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "",
            table: null,
            time: formatTime(),
            query: "",
            error: t("websocket_connection_closed_err_msg"),
          },
        ]);
      }
    },
    onMessage: (event) => {
      try {
        if (event.data) {
          const res = JSON.parse(event.data);
          console.log("res", res);

          if (!res?.success) {
            // Check if this is an endpoint timeout response that we should ignore
            if (res?.message === "Endpoint request timed out") {
              return; // Don't process this message, just ignore it
            }
            logPrompt(
              messages[messages.length - 1]?.text || "",
              res?.message || "success-0",
            );
            setMessages((prev) => [
              ...prev,
              {
                sender: "bot",
                text: t("listing_err_msg"),
                time: formatTime(),
              },
            ]);
            setLoading(false);
            // Update credits for text2SQL response (even if error)
            updateQueryCredits();
            disconnect();
            return;
          }

          handleOnMessage(res);
          // Update credits for successful text2SQL response
          updateQueryCredits();
        }
        //disconnect(); // Close after handling message
      } catch (e) {
        console.error("Invalid JSON in WebSocket message", event.data);
        logPrompt(messages[messages.length - 1]?.text || "", e?.message || "");
        disconnect();
      }
      // handleOnMessage(data); // your existing logic
    },
  });

  const fetchQueryCredits = async (isRetry = false) => {
    let credits = 0;
    try {
      const res = await post("aiConfig", {
        querystring: "get_ai_credits_data",
      });
      if (res?.success) {
        const { credits_remaining, auto_topup_enabled } = res.data;
        credits = +credits_remaining;

        // If credits are 0 and auto-topup is enabled, wait and retry (but only on first call)
        if (credits === 0 && auto_topup_enabled && !isRetry) {
          console.log(
            "Credits hit 0 with auto-topup enabled, waiting for topup...",
          );
          setIsAutoTopupInProgress(true);
          // Wait 6 seconds for auto-topup to process, then fetch again
          setTimeout(async () => {
            try {
              await fetchQueryCredits(true); // Pass isRetry=true to prevent infinite loops
            } catch (error) {
              console.error("Error during auto-topup retry:", error);
            } finally {
              setIsAutoTopupInProgress(false);
            }
          }, 6000);
        } else if (isRetry) {
          // Reset auto-topup progress state on retry
          setIsAutoTopupInProgress(false);
        }
      }
    } catch (error) {
      console.error("Error fetching query credits:", error);
      // Reset auto-topup progress state on error
      setIsAutoTopupInProgress(false);
    }
    setQueryCredits(credits);
  };

  useEffect(() => {
    //effect to set query credits
    (async () => {
      fetchQueryCredits();
    })();
  }, []);

  //effect to set credits add message
  useEffect(() => {
    if (tag === "advanced-user") {
      (async () => {
        let text = "";
        const subRes = await postApi(
          {
            querystring: "get_subscription_status",
          },
          "commonNew",
        );
        if (subRes.success) {
          const {
            read_expiration_date_sec,
            duration,
            no_of_vehicles,
            product,
          } = subRes?.subscription_data;
          let creditsInPlan = 0;
          let noOfMonths = duration === 1 ? 1 : 12;
          if (product === "5") {
            creditsInPlan = 2 * noOfMonths * no_of_vehicles;
          } else if (product === "6") {
            creditsInPlan = 5 * noOfMonths * no_of_vehicles;
          }
          let date = getListingDisplayDate(
            Math.round(read_expiration_date_sec / 1000),
          );
          text = t("count_credits_to_add_on_date_msg", {
            count: creditsInPlan,
            date: date,
          });
        }
        setCreditsAddMsg(text);
      })();
    }
  }, [tag]);

  useEffect(() => {
    if (editReport) {
      setMessages([
        {
          sender: "bot",
          text: t("report_editing_msg", { reportName: editReport?.reportName }),
          time: formatTime(),
        },
      ]);
      setConversationId(editReport.conversationId);
      setShowChat(true);
      //clearEditReport(); // sets editMessage to null/undefined
    } else {
      // Only set the welcome message if there is no existing history
      if (!messages || messages.length === 0) {
        setMessages([
          {
            sender: "bot",
            text: t("create_report_bot_welcome_msg"),
            time: formatTime(),
          },
        ]);
      }
    }
  }, [editReport]);

  //Effect to change loading message
  useEffect(() => {
    if (!loading || viewAllLoading) return;

    let step = 0;
    const interval = setInterval(() => {
      setLoadingPhase(loadingSteps[step]);
      step = (step + 1) % loadingSteps.length;
    }, 4000); // change phase every 2 seconds

    return () => clearInterval(interval); // cleanup on unmount or loading false
  }, [loading, viewAllLoading]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  // Effect to handle WebSocket connection errors
  useEffect(() => {
    if (connectionError) {
      console.error("WebSocket connection error:", connectionError);
      setLoading(false);

      // Get the last message text for logging
      const lastMessageText = messages[messages.length - 1]?.text || "";
      logPrompt(lastMessageText, connectionError || "");

      // Add error message to chat
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "",
          table: null,
          time: formatTime(),
          query: "",
          error: t("listing_err_msg"),
        },
      ]);
    }
  }, [connectionError, t]);

  function handleOnMessage(res) {
    if (res) {
      isIntentionalClosure.current = true;
      disconnect();
      console.log("Received data:", res);
      if (res?.success) {
        handleDataResponse(res?.data);
        // if (res?.view_all) {
        //   handleViewAllResponse(res?.data);
        // } else {
        //   handleDataResponse(res?.data);
        // }
        //didUnmount.current = true;
        //getWebSocket().close();
      }
      //console.log(JSON.parse(data));
    }
  }

  const updateQueryCredits = async () => {
    const res = await post("aiConfig", {
      querystring: "update_credits_remaining_count",
      credits_consumed: 1,
      screen: "ai_reports",
    });
    console.log("update credits res", res);
    if (res.success) {
      // Fetch fresh credits data, which will handle auto-topup timing
      fetchQueryCredits();
    }
  };

  //log the user input prompt along with the corresponding sql query output(Or explanation/error) to FRONTEND_API_ERRORS
  const logPrompt = async (prompt, sqlQuery) => {
    try {
      const email = auth?.currentUser?.email;
      const apiData = {
        querystring: "log_api_errors",
        errors: [
          {
            user_id: email,
            org_id: orgId,
            source: "web",
            request: prompt,
            method_type: "AI_REPORT",
            error: sqlQuery,
            placeholder: "",
            url: "",
          },
        ],
      };
      await axios.post(urlConfig.getInfoV2, apiData);
    } catch (err) {
      console.error("Failed to log prompt:", err);
    }
  };

  const getFormattedQuery = (query) => {
    // Normalize SQL tail for MySQL so we can safely append LIMIT 1001
    let base = (query || "").trim();
    // Strip trailing semicolons
    base = base.replace(/;+\s*$/i, "");
    // Strip trailing single-line comment
    base = base.replace(/\s*--[^\n]*$/i, "");
    // Strip trailing block comment
    base = base.replace(/\s*\/\*[\s\S]*?\*\/\s*$/i, "");
    // Remove trailing LIMIT [n] [OFFSET m]
    base = base.replace(/\s+LIMIT\s+\d+(\s+OFFSET\s+\d+)?\s*$/i, "");
    // Remove trailing LIMIT offset, count
    base = base.replace(/\s+LIMIT\s+\d+\s*,\s*\d+\s*$/i, "");
    // Remove trailing OFFSET m alone (some dialects allow this)
    base = base.replace(/\s+OFFSET\s+\d+\s*$/i, "");
    const fullQuery = `${base} LIMIT 1001`;
    return fullQuery;
  };

  const handleDataResponse = (data) => {
    console.log("data in handleDataResponse:", data);

    const addErrorMessage = (errorMsg) => {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "",
          table: null,
          time: formatTime(),
          query: "",
          error: errorMsg,
        },
      ]);
    };

    try {
      setConversationId(data?.conversationID || "");
      //log the user input prompt along with the corresponding sql query output(Or explanation/error) to FRONTEND_API_ERRORS
      const outputLog =
        data?.output || data?.explanation || data?.runError || data?.error;
      logPrompt(messages[messages.length - 1]?.text || "", outputLog || "");

      if (data?.runError || data?.error) {
        addErrorMessage(
          `${data?.runError || data?.error}. ${t("contact_support_msg")}`,
        );
        return;
      }

      //If output is null, assume that there is no response data
      if (!data?.output) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: data?.explanation || t("listing_err_msg"),
            time: formatTime(),
          },
        ]);
        return;
      }

      const results = data?.results;

      if (!results || !Array.isArray(results)) {
        addErrorMessage(t("listing_err_msg"));
        return;
      }

      let fullQuery = data?.output;
      if (!fullQuery?.toLowerCase().includes("limit")) {
        fullQuery = getFormattedQuery(data?.output);
      }
      setShowChat(!results?.length);

      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "",
          table: results,
          time: formatTime(),
          query: fullQuery,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // const handleViewAll = async (tableDataForThatTable, sqlQuery) => {
  //   try {
  //     // Normalize SQL tail for MySQL so we can safely append LIMIT 1001
  //     let base = (sqlQuery || "").trim();
  //     // Strip trailing semicolons
  //     base = base.replace(/;+\s*$/i, "");
  //     // Strip trailing single-line comment
  //     base = base.replace(/\s*--[^\n]*$/i, "");
  //     // Strip trailing block comment
  //     base = base.replace(/\s*\/\*[\s\S]*?\*\/\s*$/i, "");
  //     // Remove trailing LIMIT [n] [OFFSET m]
  //     base = base.replace(/\s+LIMIT\s+\d+(\s+OFFSET\s+\d+)?\s*$/i, "");
  //     // Remove trailing LIMIT offset, count
  //     base = base.replace(/\s+LIMIT\s+\d+\s*,\s*\d+\s*$/i, "");
  //     // Remove trailing OFFSET m alone (some dialects allow this)
  //     base = base.replace(/\s+OFFSET\s+\d+\s*$/i, "");
  //     const fullQuery = `${base} LIMIT 1001`;
  //     setQuery(fullQuery);
  //     if (tableDataForThatTable?.length < 20) {
  //       setTableData(tableDataForThatTable);
  //       setHasMore(false);
  //       setShowTableModal(true);
  //       return;
  //     }

  //     setViewAllLoading(true);
  //     //setLoading(true);
  //     //setLoadingPhase(t("generating_final_report"));

  //     console.log("fullQuery", fullQuery);
  //     const apiData = {
  //       querystring: "get_report_data_from_query",
  //       sql_query: fullQuery,
  //       limit: 1, //limit is true -> Apply limit of 1001
  //     };

  //     const res = await putApi(apiData, "dashboard");

  //     if (!res?.success) {
  //       setMessages((prev) => [
  //         ...prev,
  //         {
  //           sender: "bot",
  //           text: t("listing_err_msg"),
  //           time: formatTime(),
  //         },
  //       ]);
  //     }

  //     if (res?.success && Array.isArray(res.data)) {
  //       const { data, has_more } = res;
  //       setTableData(data);
  //       setShowTableModal(true);
  //       setHasMore(has_more);
  //     }

  //     setViewAllLoading(false);
  //     //setLoading(false);
  //   } catch (err) {
  //     console.error("Error sending message:", err);
  //     setMessages((prev) => [
  //       ...prev,
  //       {
  //         sender: "bot",
  //         text: t("listing_err_msg"),
  //         time: formatTime(),
  //       },
  //     ]);
  //     setViewAllLoading(false);
  //     //setLoading(false);
  //   }
  // };

  const handleViewAll = async () => {
    try {
      //get table data from the latest bot message containing table data
      const latestTableFromMessages = messages
        .slice()
        .reverse()
        .find((msg) => msg.sender === "bot" && msg.table)?.table;

      if (latestTableFromMessages?.length < 20) {
        setTableData(latestTableFromMessages);
        setHasMore(false);
        setShowTableModal(true);
        return;
      }

      //setQuery(fullQuery);

      setViewAllLoading(true);

      console.log("query", query);
      const apiData = {
        querystring: "get_report_data_from_query",
        sql_query: query,
        limit: query?.toLowerCase().includes("limit") ? 0 : 1, //if query contains limit, then limit is 0, else 1
      };

      const res = await putApi(apiData, "dashboard");

      if (!res?.success) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: t("listing_err_msg"),
            time: formatTime(),
          },
        ]);
      }

      if (res?.success && Array.isArray(res.data)) {
        const { data, has_more } = res;
        setTableData(data);
        setShowTableModal(true);
        setHasMore(has_more);
      }

      setViewAllLoading(false);
    } catch (err) {
      console.error("Error sending message:", err);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: t("listing_err_msg"),
          time: formatTime(),
        },
      ]);
      setViewAllLoading(false);
    }
  };

  const onSaveReport = async (reportName) => {
    setSaveBtnLoading(true);
    let apiData = {
      querystring: "add_ai_report",
      action: editReport ? 2 : 1,
      report_id: editReport ? editReport?.reportId : `${userId}_${Date.now()}`,
      sql_query: query,
      conversation_id: conversationId,
      report_name: reportName,
      //last_run: editReport ? editReport.last_run : 0,
      //created_at: editReport ? editReport.created_at : getUnixTime(new Date()),
    };
    // if (!editReport) {
    //   apiData = {
    //     ...apiData,
    //     last_run: 0,
    //     created_at: getUnixTime(new Date()),
    //   };
    // }
    console.log("apiData", apiData);

    try {
      const res = await putApi(apiData, "dashboard");

      if (!res.success) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: `❌ ${t("report_save_failure_msg")}`,
            time: formatTime(),
          },
        ]);
      }

      if (res?.success) {
        fetchReports();
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: t("report_save_success_msg", { reportName }),
            time: formatTime(),
          },
        ]);
        setActiveKey?.("1");
      }
    } catch (e) {
      console.error("err", e);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: `❌ ${t("report_save_failure_msg")}`,
          time: formatTime(),
        },
      ]);
    } finally {
      setSaveBtnLoading(false);
      setShowSaveReport(false);
      if (showTableModal) {
        setShowTableModal(false);
      }
    }
  };

  const formatTime = () =>
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const handleSend = async (promptText = userInput) => {
    if (!promptText.trim() || loading) return;

    //logPrompt when user sends an input message
    logPrompt(promptText, "chat-initiated");

    if (queryCredits <= 0 && !isAutoTopupInProgress) {
      if (tag === "free-user") {
        setShowSubscribeForCreditsModal(true);
      } else {
        setShowExtraCreditsModal(true);
      }
      return;
    }

    const newMessages = [
      ...messages,
      { sender: "user", text: promptText, time: formatTime() },
    ];

    setMessages(newMessages);
    setUserInput("");
    setLoading(true);
    setLoadingPhase(t("report_loading_msg_1"));

    try {
      const websocketData = {
        action: "get_text_2_sql_data",
        prompt: `${promptText}. My org_id=${orgId}.`,
        conversation_id: conversationId || "",
        //view_all: 0,
      };
      console.log("websocketData", websocketData);

      // Always connect fresh for each request to avoid connection state issues
      console.log("🔄 Connecting fresh WebSocket for new request");

      // Store the message to send when connection opens
      pendingMessageRef.current = JSON.stringify(websocketData);
      isIntentionalClosure.current = false;

      connect(); // lazy connect - message will be sent in onOpen callback
    } catch (err) {
      console.error("Error sending message:", err);
      setMessages([
        ...newMessages,
        {
          sender: "bot",
          text: t("listing_err_msg"),
          time: formatTime(),
        },
      ]);
      setLoading(false);
    }
    // finally {
    //   setLoading(false);
    // }
  };

  const handleContactSupport = async (data) => {
    if (supportSaveBtnLoading) return;

    setSupportSaveBtnLoading(true);
    const descTrimmed = data?.desc?.trim() || "";
    const emailTrimmed = data?.email?.trim() || "";
    const phoneTrimmed = data?.phone?.trim() || "";

    const apiData = {
      querystring: "send_custom_report_email",
      email_address: emailTrimmed,
      desc: descTrimmed,
      phone: phoneTrimmed,
    };
    try {
      const res = await putApi(apiData, "dashboard");
      if (res?.success) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: t("custom_report_request_success_msg"),
            time: formatTime(),
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: t("listing_err_msg"),
            time: formatTime(),
          },
        ]);
      }
    } catch (e) {
      console.log("error", e);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: t("listing_err_msg"),
          time: formatTime(),
        },
      ]);
    } finally {
      setSupportSaveBtnLoading(false);
      setShowSupportModal(false);
    }
  };

  return (
    <>
      {showSaveReport && (
        <SaveReportModal
          show={showSaveReport}
          onClose={() => setShowSaveReport(false)}
          onSaveReport={onSaveReport}
          saveBtnLoading={saveBtnLoading}
        />
      )}
      {showSupportModal && (
        <ContactSupportModal
          show={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          saveBtnLoading={supportSaveBtnLoading}
          onContactSupport={handleContactSupport}
        />
      )}
      {showExtraCreditsModal && (
        // <ExtraCreditsModal
        //   show={showExtraCreditsModal}
        //   onClose={() => setShowExtraCreditsModal(false)}
        //   saveBtnLoading={creditsSaveBtnLoading}
        //   onAddExtraCredits={handleAddExtraCredits}
        // />
        <TopUpCreditsModal
          show={showExtraCreditsModal}
          onHide={() => setShowExtraCreditsModal(false)}
          fromScreen={"reporting"}
          //onPurchase={handleAddExtraCredits}
        />
      )}
      {showSubscribeForCreditsModal && (
        <InfoPopup
          showPopup={showSubscribeForCreditsModal}
          setShowPopup={setShowSubscribeForCreditsModal}
          message={t("subscribe_for_ai_credits_msg")}
          icon={{ color: colorYellowNew, className: "fi fi-rr-info" }}
          actionIcon={{
            label: t("go_to_billing"),
            handler: () => history.push("/billing"),
          }}
        />
      )}
      <Wrapper>
        <div className="h-100 d-flex flex-column">
          {/* {chatStarted && (
            <div
              style={{
                position: "sticky",
                //top: 112,
                //right: 112,
                zIndex: 10,

                background: "#f9fafb",
                padding: "8px",
              }}
            >
              <div className="d-flex justify-content-end">
                <FilterButton onClick={resetChat}>
                  <div className="d-flex align-items-center gap-2">
                    <i
                      className="fi fi-rr-refresh"
                      style={{ margin: 0, position: "relative", top: "1px" }}
                    ></i>
                    <p>{t("new_chat")}</p>
                  </div>
                </FilterButton>
              </div>
            </div>
          )} */}
          <div
            className="flex-grow-1 overflow-auto p-3"
            style={{ background: "#f9fafb" }}
          >
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`d-flex flex-column mt-3 ${
                  msg.sender === "user"
                    ? "align-items-end"
                    : "align-items-start"
                }`}
              >
                {msg.sender === "bot" ? (
                  <div className="d-flex gap-2">
                    <div className="mt-1">
                      <RobotIcon />
                    </div>
                    <div className="d-flex flex-column">
                      {msg.text && (
                        <div
                          className={`p-2 rounded shadow-sm `}
                          style={{
                            maxWidth: "90%",
                            background: "white",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {msg.text}
                        </div>
                      )}
                      {msg.error && (
                        <div
                          className={`p-2 rounded shadow-sm `}
                          style={{
                            maxWidth: "90%",
                            background: "#fbd9d3",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {msg.error}
                        </div>
                      )}
                      {msg.table && (
                        <SampleTable
                          data={msg.table}
                          // onViewAll={() =>
                          //   //handleViewAll(msg?.table, msg?.query)
                          //   handleViewAll()
                          // }
                          onContactSupport={() => setShowSupportModal(true)}
                          //viewAllLoading={viewAllLoading}
                          //onSaveReport={() => setShowSaveReport(true)}
                        />
                      )}
                      <small className={`text-muted mt-1 mx-2`}>
                        {msg.time}
                      </small>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-end w-100">
                    {msg.text && (
                      <div
                        className={`p-2 rounded shadow-sm `}
                        //   ${
                        //     msg.sender === "user"
                        //       ? "bg-primary text-white"
                        //       : "bg-white border"
                        //   }
                        style={{
                          maxWidth: "90%",
                          background: "#E0F2FE",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {msg.text}
                      </div>
                    )}
                    <small className={`text-muted mt-1 mx-2`}>{msg.time}</small>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <LoadingContainer className="loading-phase-container my-2 d-flex align-items-center gap-2">
                <div
                  className="spinner-border spinner-border-sm text-secondary"
                  role="status"
                />
                <div key={loadingPhase} className="fade-text">
                  {loadingPhase}
                </div>
              </LoadingContainer>
            )}

            <div ref={bottomRef} />
          </div>

          {showTableModal && tableData.length && (
            <TableModal
              showModal={showTableModal}
              onClose={() => {
                setShowTableModal(false);
                setTableData([]);
                setHasMore(false);
              }}
              data={tableData}
              mode={"create-report"}
              onSaveClick={() => setShowSaveReport(true)}
              hasMore={hasMore}
              sqlQuery={query}
            />
          )}

          {messages.length <= 1 && !editReport && (
            <div className="px-4 pt-2 bg-white border-top">
              <strong>{t("try_these_examples")}</strong>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {examples.map((text, i) => (
                  <ExamplePrompt key={i} onClick={() => handleSend(text)}>
                    {text}
                  </ExamplePrompt>
                ))}
              </div>
            </div>
          )}

          {showChat ? (
            <>
              <div className="d-flex flex-column gap-1 px-3 pt-2 pb-0 bg-white">
                {/* Keep Current Report link - only show when there's data to save/view */}
                {query && !loading && (
                  <div className="d-flex justify-content-start">
                    <KeepCurrentReportLink onClick={() => setShowChat(false)}>
                      <ArrowIcon
                        className={`fi fi-rr-angle-${
                          theme.direction === "rtl" ? "left" : "right"
                        }`}
                      />
                      {t("keep_current_report")}
                    </KeepCurrentReportLink>
                  </div>
                )}
                <div className="d-flex gap-2 align-items-center">
                  <TextArea
                    ref={textAreaRef}
                    onChange={(e) => setUserInput(e.target.value)}
                    value={userInput}
                    placeholder={
                      conversationId
                        ? t("reporting_prompt_update_placeholder")
                        : t("reporting_prompt_create_placeholder")
                    }
                    rows={1}
                    // onKeyDown={(e) =>
                    //   e.key === "Enter" && !e.shiftKey && handleSend()
                    // }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault(); // 🔥 Prevent the newline from getting inserted
                        handleSend();
                      }
                    }}
                  />
                  <PromptSubmitButton
                    type="button"
                    disabled={loading || !userInput.trim()}
                    onClick={() => handleSend()}
                  >
                    <i
                      className="fi fi-rr-paper-plane"
                      style={{ position: "relative", top: "1px" }}
                    ></i>
                  </PromptSubmitButton>
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-center align-items-center pb-3">
                <span
                  style={{
                    fontWeight: 700,
                    color: "#2563eb",
                    fontSize: 12,
                  }}
                >
                  {isAutoTopupInProgress
                    ? t("auto_topup_in_progress", "Auto top-up in progress...")
                    : t("ai_credits_remaining_count", { count: queryCredits })}
                </span>
                {tag === "advanced-user" && creditsAddMsg && (
                  <>
                    {" • "}
                    <HintText className="p-0">{creditsAddMsg}</HintText>
                  </>
                )}
                {queryCredits <= 3 && (
                  <>
                    {" • "}
                    {tag === "free-user" ? (
                      <LinkSpan onClick={() => history.push("/billing")}>
                        {t("subscribe_for_ai_credits")}
                      </LinkSpan>
                    ) : (
                      <LinkSpan onClick={() => setShowExtraCreditsModal(true)}>
                        {t("top_up_ai_credits")}
                      </LinkSpan>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="d-flex gap-3 px-3 pt-3 pb-4 bg-white justify-content-center">
              <AddFormButton type="button" onClick={handleViewAll}>
                <i
                  className="fi fi-rr-chart-simple mx-2"
                  style={{ color: "white" }}
                ></i>
                {t("view_full_report")}
              </AddFormButton>
              <FilterButton
                type="button"
                onClick={() => setShowSaveReport(true)}
              >
                <i className="fi fi-rr-disk mx-2"></i>
                {t("save_report")}
              </FilterButton>
              <FilterButton type="button" onClick={() => setShowChat(true)}>
                <i className="fi fi-rr-edit mx-2"></i>
                {t("adjust_report")}
              </FilterButton>
            </div>
          )}
        </div>
      </Wrapper>
    </>
  );
};

export default CreateReport;

const TextArea = styled.textarea`
  font-weight: 400;
  padding: 6px 10px 8px 10px;
  font-size: 14px;
  border: 1px solid ${colorGray};
  border-radius: 6px;
  resize: none;
  width: 100%;
  transition: border 0.3s;
  min-height: 34px;
  &:focus {
    outline: none;
    border-color: ${colorBlue};
  }
`;

const Wrapper = styled.div`
  height: 100%;
  .loading-phase-container .fade-text {
    opacity: 0;
    animation: fadeIn 0.6s ease-in forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PromptSubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  color: white;
  height: 48px;
  width: 48px;
  min-width: 48px;
  background: ${sidenavColor};
  border: 1px solid ${sidenavColor};
  border-radius: 50%;
  padding: 0 10px;
  ${marginRight}: 4rem;
  white-space: nowrap;
  font-weight: 500;
  &:hover {
    ${(p) => !p.disabled && `background: ${sidenavLinkHoverColor}`};
  }

  ${(p) =>
    p.disabled &&
    `background: ${colorGray};
    border-color: ${colorGray};
    //background: #f2f2f2;
    //border-color: #f2f2f2;
    //color: black;
  `};

  ${(p) =>
    p.isLoading &&
    `div {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: conic-gradient(#0000 10%, #ffffff);
    -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 2px), #000 0);
    animation: s3 1s infinite linear;

    @keyframes s3 {
      to {
        transform: rotate(1turn);
      }
    }
  }
`}
`;

const ArrowIcon = styled.i`
  font-size: 12px;
  position: relative;
  top: 2px;
  color: ${colorGray};
  transition: color 0.2s ease;
`;

const LoadingContainer = styled.div`
  text-align: ${textAlign};
`;

const ExamplePrompt = styled.button`
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
  padding: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: black;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background-color: #eff6ff;
    border-color: #bfdbfe;
  }
`;

const KeepCurrentReportLink = styled.p`
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colorGray};
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #1f2937;

    ${ArrowIcon} {
      color: #1f2937;
    }
  }
`;

const RobotIcon = () => {
  return (
    <div
      style={{
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #853BE1 , #3761E3)", // purple gradient
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: "2px",
      }}
    >
      <i
        className="fi fi-rr-robot"
        style={{
          color: "white",
          fontSize: "14px",
          position: "relative",
          top: "1px",
        }}
      ></i>
    </div>
  );
};
